/**
 * Platform Admin · Payments
 *
 * Unified payment history (signup, pack purchases, offline) with filters,
 * a totals card, CSV export, and the ability to record an offline payment.
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Receipt, FunnelSimple, ArrowsClockwise, DownloadSimple, Plus, CaretLeft, CaretRight, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_TONE = {
    signup: "border-primary/40 text-primary",
    pack: "border-success text-success",
    offline: "border-muted-foreground text-muted-foreground",
};
const STATUS_TONE = {
    paid: "border-success text-success",
    pending: "border-warning text-warning",
    failed: "border-destructive text-destructive",
};

const BASE = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function PlatformPaymentsPage() {
    const [filters, setFilters] = useState({ kind: "__all__", status: "__all__", org_id: "__all__", date_from: "", date_to: "", q: "" });
    const [orgs, setOrgs] = useState([]);
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState({ paid_inr: 0, pending_inr: 0 });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(25);
    const [loading, setLoading] = useState(false);
    const [offlineOpen, setOfflineOpen] = useState(false);
    const [pricing, setPricing] = useState(null);
    const [offlineForm, setOfflineForm] = useState({ org_id: "", amount_inr: "", pack_count: 1, reference: "", note: "" });
    const [submitting, setSubmitting] = useState(false);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    const params = () => {
        const p = new URLSearchParams();
        if (filters.kind !== "__all__") p.set("kind", filters.kind);
        if (filters.status !== "__all__") p.set("status", filters.status);
        if (filters.org_id !== "__all__") p.set("org_id", filters.org_id);
        if (filters.date_from) p.set("date_from", filters.date_from);
        if (filters.date_to) p.set("date_to", filters.date_to);
        if (filters.q) p.set("q", filters.q);
        return p;
    };

    const load = async (resetPage = false) => {
        const pp = resetPage ? 1 : page;
        if (resetPage) setPage(1);
        setLoading(true);
        try {
            const p = params();
            p.set("page", String(pp)); p.set("limit", String(limit));
            const { data } = await api.get(`/platform/payments?${p.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
            setTotals(data.totals || { paid_inr: 0, pending_inr: 0 });
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            try {
                const [{ data: o }, { data: pr }] = await Promise.all([
                    api.get("/platform/organizations?page=1&limit=500"),
                    api.get("/platform/pricing").catch(() => ({ data: null })),
                ]);
                setOrgs((o && o.items) || o || []);
                if (pr) setPricing(pr);
            } catch (e) { console.warn("[payments] failed to load orgs/pricing:", e); }
        })();
    }, []);

    useEffect(() => { load(false); /* eslint-disable-next-line */ }, [page]);
    useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);

    const reset = () => {
        setFilters({ kind: "__all__", status: "__all__", org_id: "__all__", date_from: "", date_to: "", q: "" });
        setPage(1);
        setTimeout(() => load(true), 0);
    };

    const exportCsv = () => {
        const p = params();
        const url = `${BASE}/api/platform/payments/export.csv?${p.toString()}`;
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.click();
    };

    const openOffline = (orgId) => {
        setOfflineForm({ org_id: orgId || "", amount_inr: pricing ? String((pricing.monthly_price || 0) * 12) : "", pack_count: 1, reference: "", note: "" });
        setOfflineOpen(true);
    };

    const recordOffline = async () => {
        if (!offlineForm.org_id) return toast.error("Pick an organization");
        if (!offlineForm.amount_inr) return toast.error("Enter the amount in INR");
        setSubmitting(true);
        try {
            await api.post("/platform/payments/offline", {
                org_id: offlineForm.org_id,
                amount_inr: Number(offlineForm.amount_inr),
                pack_count: Number(offlineForm.pack_count) || 1,
                reference: offlineForm.reference || null,
                note: offlineForm.note || null,
            });
            toast.success("Offline payment recorded");
            setOfflineOpen(false);
            await load(true);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSubmitting(false); }
    };

    const revertOffline = async (id) => {
        if (!confirm("Revert this offline payment? Packs added by it will be removed.")) return;
        try {
            await api.delete(`/platform/payments/offline/${id}`);
            toast.success("Reverted");
            load(false);
        } catch (e) { toast.error(extractError(e)); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Payments"
                description="Every Zoho-mediated subscription / expansion-pack payment plus any offline payments recorded by you."
                testid="platform-payments-header"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="platform-payments-totals">
                <div className="brut-section p-5">
                    <div className="label-overline mb-2">Paid (filtered)</div>
                    <div className="text-3xl font-display font-bold text-success">{inr(totals.paid_inr)}</div>
                </div>
                <div className="brut-section p-5">
                    <div className="label-overline mb-2">Pending</div>
                    <div className="text-3xl font-display font-bold text-warning">{inr(totals.pending_inr)}</div>
                </div>
                <div className="brut-section p-5 flex flex-col">
                    <div className="label-overline mb-2">Actions</div>
                    <div className="flex flex-col gap-2 mt-auto">
                        <Button onClick={() => openOffline()} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="record-offline-btn"><Plus size={14} className="mr-1" /> Record offline payment</Button>
                        <Button variant="outline" onClick={exportCsv} className="rounded-sm" data-testid="export-csv-btn"><DownloadSimple size={14} className="mr-1" /> Export CSV</Button>
                    </div>
                </div>
            </div>

            <div className="brut-section p-4 mb-6" data-testid="platform-payments-filters">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                    <div>
                        <Label className="label-overline flex items-center gap-1"><FunnelSimple size={11} /> Kind</Label>
                        <Select value={filters.kind} onValueChange={(v) => setFilters({ ...filters, kind: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid="filter-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All</SelectItem>
                                <SelectItem value="signup">Signup</SelectItem>
                                <SelectItem value="pack">Pack purchase</SelectItem>
                                <SelectItem value="offline">Offline</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-overline">Status</Label>
                        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Any</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-overline">Organization</Label>
                        <Select value={filters.org_id} onValueChange={(v) => setFilters({ ...filters, org_id: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid="filter-org"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-60">
                                <SelectItem value="__all__">All orgs</SelectItem>
                                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-overline">From</Label>
                        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-2 rounded-sm" data-testid="filter-from" />
                    </div>
                    <div>
                        <Label className="label-overline">To</Label>
                        <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-2 rounded-sm" data-testid="filter-to" />
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => load(true)} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground flex-1" data-testid="apply-filters"><FunnelSimple size={14} className="mr-1" /> Apply</Button>
                        <Button variant="outline" onClick={reset} className="rounded-sm" title="Reset" data-testid="reset-filters"><ArrowsClockwise size={14} /></Button>
                    </div>
                </div>
                <div className="mt-3">
                    <Label className="label-overline">Search (org or email)</Label>
                    <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load(true)} className="mt-2 rounded-sm" placeholder="Search…" data-testid="filter-q" />
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <EmptyState icon={Receipt} title="No payments yet" description="No payments match your filters." testid="platform-payments-empty" />
            ) : (
                <>
                    <div className="brut-section overflow-x-auto">
                        <table className="w-full" data-testid="payments-table">
                            <thead><tr>
                                <th className="data-header text-left">When</th>
                                <th className="data-header text-left">Org</th>
                                <th className="data-header text-left">Kind</th>
                                <th className="data-header text-left">Payer</th>
                                <th className="data-header text-right">Amount</th>
                                <th className="data-header text-left">Status</th>
                                <th className="data-header text-left">Provider</th>
                                <th className="data-header text-left">Payment ID</th>
                                <th className="data-header"></th>
                            </tr></thead>
                            <tbody>
                                {items.map((p, i) => (
                                    <tr key={`${p.kind}-${p.id}`} className="hover:bg-secondary/50" data-testid={`payment-row-${i}`}>
                                        <td className="data-cell font-mono text-xs whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                                        <td className="data-cell text-sm font-medium">{p.org_name || <span className="text-muted-foreground italic">unprovisioned</span>}</td>
                                        <td className="data-cell"><Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${KIND_TONE[p.kind] || ""}`}>{p.kind}</Badge></td>
                                        <td className="data-cell text-xs">{p.payer_email || p.payer_name || "—"}</td>
                                        <td className="data-cell font-mono text-sm text-right">{inr(p.amount_inr)}</td>
                                        <td className="data-cell">
                                            <Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${STATUS_TONE[p.status] || ""}`}>{p.status}</Badge>
                                            {p.demo && <span className="ml-1 text-[9px] font-mono opacity-60">demo</span>}
                                        </td>
                                        <td className="data-cell text-[10px] font-mono uppercase">{p.provider}</td>
                                        <td className="data-cell">
                                            <code className="text-[10px] font-mono text-muted-foreground" title={p.payment_id || ""}>{p.payment_id ? `${p.payment_id.slice(0, 14)}…` : "—"}</code>
                                            {p.reference && <div className="text-[10px] text-muted-foreground">ref: {p.reference}</div>}
                                        </td>
                                        <td className="data-cell text-right">
                                            {p.kind === "offline" && (
                                                <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => revertOffline(p.id)} data-testid={`revert-offline-${p.id}`} title="Revert offline payment">
                                                    <Trash size={14} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between mt-4 text-sm">
                        <div className="text-muted-foreground">Showing <span className="font-mono">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of <span className="font-mono">{total}</span></div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="pay-prev"><CaretLeft size={14} /></Button>
                            <span className="font-mono text-xs">Page {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="pay-next"><CaretRight size={14} /></Button>
                        </div>
                    </div>
                </>
            )}

            {/* Offline payment dialog */}
            <Dialog open={offlineOpen} onOpenChange={setOfflineOpen}>
                <DialogContent className="rounded-sm" data-testid="offline-payment-dialog">
                    <DialogHeader><DialogTitle>Record offline payment</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <p className="text-xs text-muted-foreground">Use this when an org has paid you via bank transfer / cheque / cash. It bumps their expansion-pack count and reactivates the org if suspended.</p>
                        <div>
                            <Label className="label-overline">Organization</Label>
                            <Select value={offlineForm.org_id || "__none__"} onValueChange={(v) => setOfflineForm({ ...offlineForm, org_id: v === "__none__" ? "" : v })}>
                                <SelectTrigger className="mt-2 rounded-sm" data-testid="offline-org-select"><SelectValue placeholder="Pick organization" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    <SelectItem value="__none__" disabled>Pick organization</SelectItem>
                                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Amount (INR)</Label>
                                <Input type="number" min={1} value={offlineForm.amount_inr} onChange={(e) => setOfflineForm({ ...offlineForm, amount_inr: e.target.value })} className="mt-2 rounded-sm" data-testid="offline-amount" />
                            </div>
                            <div>
                                <Label className="label-overline">Pack count</Label>
                                <Input type="number" min={1} max={200} value={offlineForm.pack_count} onChange={(e) => setOfflineForm({ ...offlineForm, pack_count: e.target.value })} className="mt-2 rounded-sm" data-testid="offline-packs" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">Reference / Receipt #</Label>
                            <Input value={offlineForm.reference} onChange={(e) => setOfflineForm({ ...offlineForm, reference: e.target.value })} className="mt-2 rounded-sm" placeholder="UTR / cheque no. (optional)" data-testid="offline-reference" />
                        </div>
                        <div>
                            <Label className="label-overline">Note</Label>
                            <Textarea value={offlineForm.note} onChange={(e) => setOfflineForm({ ...offlineForm, note: e.target.value })} className="mt-2 rounded-sm" placeholder="Internal notes (optional)" data-testid="offline-note" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOfflineOpen(false)} className="rounded-sm" data-testid="offline-cancel">Cancel</Button>
                            <Button onClick={recordOffline} disabled={submitting} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="offline-save">
                                {submitting ? "Saving…" : "Record payment"}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
